/**
 * Table-oriented PowerPoint tools: add a table and set individual cell text.
 *
 * Handlers follow the PowerPoint.run/load/sync conventions documented in
 * pptx-tools.ts.
 */

import type { PptxTool } from "../tool-types";

export const tableTools: PptxTool[] = [
  // ─── Add Table ────────────────────────────────────────────────────────────

  {
    schema: {
      name: "add_table",
      description:
        "Add a table to a slide with the specified number of rows and columns.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide.",
          },
          rows: {
            type: "number",
            description: "Number of rows in the table.",
          },
          columns: {
            type: "number",
            description: "Number of columns in the table.",
          },
          left: {
            type: "number",
            description: "Horizontal position in points. Default: 50.",
          },
          top: {
            type: "number",
            description: "Vertical position in points. Default: 100.",
          },
          width: {
            type: "number",
            description: "Width of the table in points. Default: 500.",
          },
          height: {
            type: "number",
            description: "Height of the table in points. Default: 200.",
          },
        },
        required: ["index", "rows", "columns"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);

        const left    = (args.left    as number | undefined) ?? 50;
        const top     = (args.top     as number | undefined) ?? 100;
        const width   = (args.width   as number | undefined) ?? 500;
        const height  = (args.height  as number | undefined) ?? 200;

        slide.shapes.addTable(
          args.rows as number,
          args.columns as number,
          { left, top, width, height }
        );

        await context.sync();

        return { success: true };
      });
    },
  },

  // ─── Set Table Data ───────────────────────────────────────────────────────

  {
    schema: {
      name: "set_table_data",
      description:
        "Set the text content of a specific table cell on a slide. The table is identified by its shape index.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: {
            type: "number",
            description: "0-based index of the slide.",
          },
          shapeIndex: {
            type: "number",
            description: "0-based index of the table shape on the slide.",
          },
          row: {
            type: "number",
            description: "0-based row index.",
          },
          column: {
            type: "number",
            description: "0-based column index.",
          },
          text: {
            type: "string",
            description: "Text to set in the cell.",
          },
        },
        required: ["slideIndex", "shapeIndex", "row", "column", "text"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);
        const tableShape = slide.shapes.getItemAt(args.shapeIndex as number);
        const table = tableShape.getTable();

        const cell = table.getCellOrNullObject(
          args.row as number,
          args.column as number
        );

        cell.text = args.text as string;

        await context.sync();

        return { success: true };
      });
    },
  },
];
